import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { sampleEdgePoints } from '../pointCloudUtils';
import { EV_DIM, EV_PRIMARY, EV_GLOW } from '../twinColors';
const STATION_COUNT = 4;
const SPACING = 3.5;

/**
 * EVCharger - Point cloud charging stations with vehicle silhouettes
 * and active charger glow.
 */
export default function EVCharger({ state, position, onClick }) {
    const glowRefs = useRef([]);

    const vehiclesCharging = state?.vehicles_charging ?? 0;

    // Sample points for post and pad edges (dispose intermediate geometries)
    const { postPoints, padPoints } = useMemo(() => {
        const postBox = new THREE.BoxGeometry(0.3, 2.2, 0.3);
        const postEdges = new THREE.EdgesGeometry(postBox);
        const padBox = new THREE.BoxGeometry(1.2, 0.1, 2.5);
        const padEdges = new THREE.EdgesGeometry(padBox);
        const result = {
            postPoints: sampleEdgePoints(postEdges, 50, 0.03),
            padPoints: sampleEdgePoints(padEdges, 40, 0.03),
        };
        postBox.dispose(); postEdges.dispose();
        padBox.dispose(); padEdges.dispose();
        return result;
    }, []);

    // Station X positions centered
    const stationXs = useMemo(() => {
        const totalWidth = (STATION_COUNT - 1) * SPACING;
        return Array.from({ length: STATION_COUNT }, (_, i) => i * SPACING - totalWidth / 2);
    }, []);

    // Vehicle silhouette points (scattered along the profile)
    const vehiclePoints = useMemo(() => {
        const profile = [
            [-1.2, 0.4], [-0.6, 0.8], [-0.3, 1.0],
            [0.5, 1.0], [0.8, 0.7], [1.2, 0.4],
        ];
        const pts = [];
        for (let seg = 0; seg < profile.length - 1; seg++) {
            for (let i = 0; i < 12; i++) {
                const t = Math.random();
                pts.push(
                    profile[seg][0] + (profile[seg + 1][0] - profile[seg][0]) * t + (Math.random() - 0.5) * 0.03,
                    profile[seg][1] + (profile[seg + 1][1] - profile[seg][1]) * t + (Math.random() - 0.5) * 0.03,
                    (Math.random() - 0.5) * 0.03
                );
            }
        }
        return new Float32Array(pts);
    }, []);

    // Cable arc points per station
    const cablePoints = useMemo(() => {
        return stationXs.map(() => {
            const pts = [];
            for (let i = 0; i < 15; i++) {
                const t = i / 14;
                // Quadratic arc: post top → mid arc → vehicle roof
                const y = 2.2 + t * (1.0 - 2.2) + Math.sin(t * Math.PI) * 0.6;
                const z = t * 1.2;
                pts.push(
                    (Math.random() - 0.5) * 0.03,
                    y + (Math.random() - 0.5) * 0.03,
                    z + (Math.random() - 0.5) * 0.03
                );
            }
            return new Float32Array(pts);
        });
    }, [stationXs]);

    useFrame(({ clock }) => {
        const t = clock.elapsedTime;
        for (let i = 0; i < STATION_COUNT; i++) {
            const ref = glowRefs.current[i];
            if (ref) {
                const isStationActive = i < vehiclesCharging;
                ref.material.opacity = isStationActive
                    ? 0.1 + Math.sin(t * 3 + i) * 0.05
                    : 0;
            }
        }
    });

    return (
        <group position={position} onClick={onClick}>
            {stationXs.map((sx, i) => {
                const isStationActive = i < vehiclesCharging;
                return (
                    <group key={`station-${i}`}>
                        {/* Post particles */}
                        <points position={[sx, 1.1, 0]}>
                            <bufferGeometry>
                                <bufferAttribute
                                    attach="attributes-position"
                                    count={postPoints.length / 3}
                                    array={postPoints}
                                    itemSize={3}
                                />
                            </bufferGeometry>
                            <pointsMaterial
                                color={isStationActive ? EV_PRIMARY : EV_DIM}
                                size={0.08}
                                transparent
                                opacity={isStationActive ? 0.6 : 0.2}
                                depthWrite={false}
                                sizeAttenuation
                                blending={THREE.AdditiveBlending}
                            />
                        </points>

                        {/* Pad particles */}
                        <points position={[sx, 0.05, 0]}>
                            <bufferGeometry>
                                <bufferAttribute
                                    attach="attributes-position"
                                    count={padPoints.length / 3}
                                    array={padPoints}
                                    itemSize={3}
                                />
                            </bufferGeometry>
                            <pointsMaterial
                                color={EV_DIM}
                                size={0.06}
                                transparent
                                opacity={isStationActive ? 0.4 : 0.15}
                                depthWrite={false}
                                sizeAttenuation
                                blending={THREE.AdditiveBlending}
                            />
                        </points>

                        {/* Active charger glow */}
                        <mesh
                            ref={(el) => { glowRefs.current[i] = el; }}
                            position={[sx, 1.1, 0]}
                        >
                            <boxGeometry args={[0.5, 2.4, 0.5]} />
                            <meshBasicMaterial
                                color={EV_PRIMARY}
                                transparent
                                opacity={0}
                                depthWrite={false}
                                blending={THREE.AdditiveBlending}
                            />
                        </mesh>

                        {/* Vehicle silhouette particles (active only) */}
                        {isStationActive && (
                            <points position={[sx, 0, 1.2]}>
                                <bufferGeometry>
                                    <bufferAttribute
                                        attach="attributes-position"
                                        count={vehiclePoints.length / 3}
                                        array={vehiclePoints}
                                        itemSize={3}
                                    />
                                </bufferGeometry>
                                <pointsMaterial
                                    color={EV_PRIMARY}
                                    size={0.08}
                                    transparent
                                    opacity={0.5}
                                    depthWrite={false}
                                    sizeAttenuation
                                    blending={THREE.AdditiveBlending}
                                />
                            </points>
                        )}

                        {/* Cable arc particles (active only) */}
                        {isStationActive && (
                            <points position={[sx, 0, 0]}>
                                <bufferGeometry>
                                    <bufferAttribute
                                        attach="attributes-position"
                                        count={cablePoints[i].length / 3}
                                        array={cablePoints[i]}
                                        itemSize={3}
                                    />
                                </bufferGeometry>
                                <pointsMaterial
                                    color={EV_PRIMARY}
                                    size={0.06}
                                    transparent
                                    opacity={0.4}
                                    depthWrite={false}
                                    sizeAttenuation
                                    blending={THREE.AdditiveBlending}
                                />
                            </points>
                        )}
                    </group>
                );
            })}
        </group>
    );
}
